<template>
    <div class="tw-min-h-[calc(100vh-var(--header-height,0px))] tw-bg-gradient-to-br tw-from-orange-50 tw-via-white tw-to-amber-50 tw-px-4 tw-py-8 md:tw-px-8">
        <div v-if="isPermitted == false" class="tw-rounded-lg tw-border tw-border-red-200 tw-bg-white tw-p-8 tw-text-center tw-shadow-sm">
            <h3 class="tw-m-0 tw-text-2xl tw-font-bold tw-text-gray-900">
                Not Permitted
            </h3>
            <p class="tw-mt-3 tw-text-sm tw-text-gray-600">
                You are not allowed to access this E-Invoice Request.
            </p>
            <a href="/my-orders" class="tw-mt-6 tw-inline-flex tw-rounded-md tw-bg-orange-500 tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white">
                Back to My Orders
            </a>
        </div>

        <div
            v-else-if="isExpired"
            class="tw-rounded-xl tw-border tw-border-amber-200 tw-bg-amber-50 tw-p-8 tw-text-center tw-shadow-sm"
        >
            <div class="tw-mx-auto tw-mb-5 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-100">
                <svg
                    class="tw-h-7 tw-w-7 tw-text-amber-600"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            </div>

            <h3 class="tw-m-0 tw-text-2xl tw-font-bold tw-text-gray-900">
                E-Invoice Request Expired
            </h3>

            <p class="tw-mx-auto tw-mt-3 tw-max-w-xl tw-text-sm tw-leading-6 tw-text-gray-600">
                This e-Invoice request has expired and can no longer be submitted.
                Please return to My Orders or contact support if you need further assistance.
            </p>

            <div class="tw-mt-6 tw-flex tw-justify-center">
                <a
                    href="/my-orders"
                    class="tw-inline-flex tw-min-h-11 tw-items-center tw-justify-center tw-rounded-md tw-bg-orange-500 tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white tw-no-underline tw-shadow-sm tw-transition hover:tw-bg-orange-600 hover:tw-no-underline"
                >
                    Back to My Orders
                </a>
            </div>
        </div>

        <div v-else-if="this.formData.submitted_in_consolidated_einvoice" class="tw-rounded-lg tw-border tw-border-red-200 tw-bg-white tw-p-8 tw-text-center tw-shadow-sm">
            <h3 class="tw-m-0 tw-text-2xl tw-font-bold tw-text-gray-900">
                E-Invoice Already Consolidated
            </h3>
            <p class="tw-mt-3 tw-text-sm tw-text-gray-600">
                This invoice has been consolidated. Individual submissions are no longer allowed.
            </p>
            <a href="/my-orders" class="tw-mt-6 tw-inline-flex tw-rounded-md tw-bg-orange-500 tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white">
                Back to My Orders
            </a>
        </div>

        <div v-else-if="isPermitted == true" class="tw-mx-auto tw-w-full tw-max-w-3xl">
            <div class="tw-mb-6 tw-rounded-lg tw-border tw-border-orange-100 tw-bg-white/90 tw-p-5 tw-shadow-sm md:tw-p-6">
                <div class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                    <div>
                        <p class="tw-mb-2 tw-text-sm tw-font-semibold tw-text-orange-600">WinHoo E-Invoice</p>
                        <h4 class="tw-m-0 tw-text-2xl tw-font-bold tw-leading-tight tw-text-gray-950 md:tw-text-3xl">
                            E-Invoice Request
                        </h4>
                        <p class="tw-mt-2 tw-mb-0 tw-max-w-2xl tw-text-sm tw-leading-6 tw-text-gray-600">
                            Request your e-invoice by completing the information below.
                        </p>
                    </div>
                    <img v-if="logo_file_path" :src="logo_file_path" alt="Logo" class="tw-h-12 tw-w-auto tw-object-contain" />
                </div>
            </div>

            <div v-if="isSubmitted" class="tw-mb-4 tw-rounded-lg tw-border tw-border-green-200 tw-bg-green-50 tw-p-4 tw-text-center tw-text-green-800">
                <div class="tw-mx-auto tw-mb-2 tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-green-600 tw-text-white">
                    <i class="pi pi-check tw-text-lg"></i>
                </div>
                <p class="tw-m-0 tw-text-sm tw-font-semibold">
                    Congratulations! Your E-Invoice request has been successfully submitted.
                </p>
            </div>

            <form @submit.prevent="submitForm" novalidate class="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-5 tw-shadow-sm md:tw-p-6">
                <section class="tw-grid tw-gap-4">
                    <h4 class="tw-m-0 tw-font-bold tw-uppercase tw-tracking-normal tw-text-gray-900">Invoice Details</h4>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="pos_invoice_no" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Order ID <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" v-model="formData.voucher_no" placeholder="POS Invoice No" readonly />
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700" for="date">
                                Transaction Date <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" v-model="formData.transaction_date" placeholder="Date" readonly />
                        </div>
                    </div>
                </section>

                <hr class="tw-my-6 tw-border-gray-200" />

                <section class="tw-grid tw-gap-4">
                    <h4 class="tw-m-0 tw-font-bold tw-uppercase tw-tracking-normal tw-text-gray-900">Personal Details</h4>
                    <div class="tw-flex tw-flex-col">
                        <label for="full_name" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                            Full Name (as per ID) <span class="tw-text-red-500">*</span>
                        </label>
                        <InputText class="tw-flex-1" :class="inputErrorClass('full_name')" type="text" v-model="formData.full_name" placeholder="e.g. Syatifah binti Ibrahim" required :disabled="isSubmitted" @input="clearFieldError('full_name')" />
                        <p v-if="form_error_data.full_name" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.full_name }}</p>
                    </div>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="email-field" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Email Address <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText id="email-field" type="text" v-model="formData.email_address" placeholder="e.g. syatifah@gmail.com" required :disabled="isSubmitted" :class="inputErrorClass('email_address')" @input="clearFieldError('email_address')" />
                            <p v-if="form_error_data.email_address" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.email_address }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label for="phone" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Phone Number <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText id="phone" type="tel" class="tw-w-full" :class="inputErrorClass('phone_number')" :disabled="isSubmitted" required />
                            <p v-if="form_error_data.phone_number" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.phone_number }}</p>
                        </div>
                    </div>

                    <div class="tw-space-y-2">
                        <label for="customer_type" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">Claim as:</label>
                        <div class="tw-grid tw-gap-1 sm:tw-grid-cols-2">
                            <label for="individual" class="tw-choice-card" :class="choiceClass('customer_type', formData.customer_type === 'Individual')">
                                <input class="tw-radio" type="radio" name="customer_type" value="Individual" id="individual" v-model="formData.customer_type" required :disabled="isSubmitted" @change="clearFieldError('customer_type')" />
                                <span>Individual</span>
                            </label>
                            <label for="company" class="tw-choice-card" :class="choiceClass('customer_type', formData.customer_type === 'Company')">
                                <input class="tw-radio" type="radio" name="customer_type" value="Company" id="company" v-model="formData.customer_type" required :disabled="isSubmitted" @change="clearFieldError('customer_type')" />
                                <span>Company</span>
                            </label>
                        </div>
                        <p v-if="form_error_data.customer_type" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.customer_type }}</p>
                    </div>
                </section>

                <hr v-if="formData.customer_type" class="tw-my-6 tw-border-gray-200" />

                <section v-if="formData.customer_type === 'Individual'" class="tw-grid tw-gap-4">
                    <h4 class="tw-m-0 tw-font-bold tw-uppercase tw-tracking-normal tw-text-gray-900">Individual Details</h4>
                    <div class="tw-flex tw-flex-col">
                        <label for="individual-type" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                            Individual ID Type<span class="tw-text-red-500">*</span>
                        </label>
                        <div class="tw-grid tw-gap-1 sm:tw-grid-cols-3">
                            <label for="NRIC" class="tw-choice-card" :class="choiceClass('identification_number_type', formData.identification_number_type === 'MyKad')">
                                <input class="tw-radio" type="radio" name="identification_number_type" value="MyKad" id="NRIC" v-model="formData.identification_number_type" required :disabled="isSubmitted" @change="clearFieldError('identification_number_type')" />
                                <span>MyKad</span>
                            </label>
                            <label for="Passport" class="tw-choice-card" :class="choiceClass('identification_number_type', formData.identification_number_type === 'Passport')">
                                <input class="tw-radio" type="radio" name="identification_number_type" id="Passport" value="Passport" v-model="formData.identification_number_type" required :disabled="isSubmitted" @change="clearFieldError('identification_number_type')" />
                                <span>Passport</span>
                            </label>
                            <label for="Army" class="tw-choice-card" :class="choiceClass('identification_number_type', formData.identification_number_type === 'MyTentera')">
                                <input class="tw-radio" type="radio" name="identification_number_type" id="Army" value="MyTentera" v-model="formData.identification_number_type" required :disabled="isSubmitted" @change="clearFieldError('identification_number_type')" />
                                <span>MyTentera</span>
                            </label>
                        </div>
                        <p v-if="form_error_data.identification_number_type" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.identification_number_type }}</p>
                    </div>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="phone" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Individual ID <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" @input="handleDashlessInput($event, 'identification_number')" v-model="formData.identification_number" :placeholder="getPlaceholder(formData.identification_number_type)" :required="formData.identification_number_type" :disabled="isSubmitted || formData.identification_number_type === ''" id="identification_number" class="tw-form-input" :class="inputErrorClass('identification_number')" />
                            <p v-if="form_error_data.identification_number" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.identification_number }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label for="individual_tin" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Individual Tax Identification Number (TIN) <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" @input="handleDashlessInput($event, 'individual_tin')" v-model="formData.individual_tin" placeholder="e.g. C1212341234" :disabled="isSubmitted" id="individual_tin" required class="tw-form-input" :class="inputErrorClass('individual_tin')" />
                            <p v-if="form_error_data.individual_tin" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.individual_tin }}</p>
                        </div>
                    </div>
                </section>

                <section v-else-if="formData.customer_type === 'Company'" class="tw-grid tw-gap-4">
                    <h4 class="tw-m-0 tw-font-bold tw-uppercase tw-tracking-normal tw-text-gray-900">Company Details</h4>
                    <div class="tw-flex tw-flex-col">
                        <label for="company-name" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                            Company Name <span class="tw-text-red-500">*</span>
                        </label>
                        <InputText id="company-name" type="text" v-model="formData.company_name" placeholder="e.g. Coffee Shop Sdn. Bhd." required :disabled="isSubmitted" class="tw-form-input" :class="inputErrorClass('company_name')" @input="clearFieldError('company_name')" />
                        <p v-if="form_error_data.company_name" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.company_name }}</p>
                    </div>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="company_id" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Business Registration Number (BRN) <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" @input="handleDashlessInput($event, 'company_id')" v-model="formData.company_id" placeholder="e.g. 201909123456" required :disabled="isSubmitted" id="company_id" class="tw-form-input" :class="inputErrorClass('company_id')" />
                            <p v-if="form_error_data.company_id" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.company_id }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label for="company_tin" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Company Tax Identification Number (TIN) <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" @input="handleDashlessInput($event, 'company_tin')" v-model="formData.company_tin" placeholder="e.g. C1212341234" :disabled="isSubmitted" id="company_tin" required class="tw-form-input" :class="inputErrorClass('company_tin')" />
                            <p v-if="form_error_data.company_tin" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.company_tin }}</p>
                        </div>
                    </div>
                    <div class="tw-flex tw-flex-col">
                        <label for="company_sst_number" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">SST Registration Number (Mandatory for SST-registrant)</label>
                        <InputText type="text" @input="handleDashlessInput($event, 'company_sst_number')" v-model="formData.sst_number" placeholder="e.g. X89-2104-12345678" :disabled="isSubmitted" id="company_sst_number" class="tw-form-input" :class="inputErrorClass('company_sst_number')" />
                        <p v-if="form_error_data.company_sst_number" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.company_sst_number }}</p>
                    </div>
                </section>

                <hr class="tw-my-6 tw-border-gray-200" />

                <section class="tw-grid tw-gap-4">
                    <h4 class="tw-m-0 tw-font-bold tw-uppercase tw-tracking-normal tw-text-gray-900">Address Details</h4>
                    <div class="tw-flex tw-flex-col">
                        <label for="address_line_1" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                            Address Line 1 <span class="tw-text-red-500">*</span>
                        </label>
                        <InputText id="address_line_1" type="text" v-model="formData.address_line_1" placeholder="e.g. Unit No, Street Name" :disabled="isSubmitted" required class="tw-form-input" :class="inputErrorClass('address_line_1')" @input="clearFieldError('address_line_1')" />
                        <p v-if="form_error_data.address_line_1" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.address_line_1 }}</p>
                    </div>
                    <div class="tw-flex tw-flex-col">
                        <label for="address_line_2" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">Address Line 2</label>
                        <InputText id="address_line_2" type="text" v-model="formData.address_line_2" placeholder="e.g. Unit No, Street Name" :disabled="isSubmitted" class="tw-form-input" />
                    </div>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="country" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Country <span class="tw-text-red-500">*</span>
                            </label>
                            <Select pt:root:class="tw-w-full" :disabled="isSubmitted" v-model="formData.country" :options="countries_list" filter optionLabel="name" optionValue="name" placeholder="Select a Country" class="tw-w-full" :class="inputErrorClass('country')" @change="clearFieldError('country')" />
                            <p v-if="form_error_data.country" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.country }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label for="state" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                State <span class="tw-text-red-500">*</span>
                            </label>
                            <Select pt:root:class="tw-w-full" :disabled="isSubmitted || formData.country != 'Malaysia'" v-model="formData.state" :options="filteredStateOptions" filter optionLabel="state" optionValue="name" placeholder="Select a State" class="tw-w-full" :class="inputErrorClass('state')" @change="clearFieldError('state')" />
                            <p v-if="form_error_data.state" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.state }}</p>
                        </div>
                    </div>
                    <div class="tw-grid tw-gap-4 md:tw-grid-cols-2">
                        <div class="tw-flex tw-flex-col">
                            <label for="city" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                City <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" v-if="(formData.state === '' || formData.state === '17') && formData.country === 'Malaysia'" placeholder="Please select a state in Malaysia" disabled class="tw-form-input" />
                            <InputText type="text" v-else-if="formData.state === '17' && formData.country !== 'Malaysia'" v-model="formData.city" required :disabled="isSubmitted" placeholder="e.g. New York" class="tw-form-input" :class="inputErrorClass('city')" @input="clearFieldError('city')" />
                            <Select 
                                v-else
                                v-model="formData.city"
                                required
                                :disabled="isSubmitted || !(formData.state !== '' && formData.state !== '17' && formData.country === 'Malaysia')"
                                class="tw-form-input"
                                :class="inputErrorClass('city')"
                                :placeholder="cityPlaceholder"
                                :options="cities()"
                                @change="clearFieldError('city')"
                            >
                                <!-- <option v-for="city, index in cities()" :key="index" :value="city">{{ city }}</option> -->
                            </Select>
                            <p v-if="form_error_data.city" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.city }}</p>
                        </div>
                        <div class="tw-flex tw-flex-col">
                            <label for="postal_code" class="tw-block tw-text-sm tw-font-semibold tw-text-gray-700">
                                Postal Code <span class="tw-text-red-500">*</span>
                            </label>
                            <InputText type="text" id="postal_code" v-model="formData.postal_code" placeholder="e.g. 43200" required :disabled="isSubmitted" class="tw-form-input" :class="inputErrorClass('postal_code')" @input="clearFieldError('postal_code')" />
                            <p v-if="form_error_data.postal_code" class="tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.postal_code }}</p>
                        </div>
                    </div>
                </section>

                <div class="tw-mt-6 tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-1 tw-rounded-lg tw-border tw-bg-gray-50 tw-p-4" :class="form_error_data.agreement ? 'tw-border-red-600' : 'tw-border-gray-200'">
                    <input type="checkbox" id="agreement" v-model="formData.accept_privacy_policy_and_tac" required :disabled="isSubmitted" class="tw-mt-1 tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-orange-600 focus:tw-ring-orange-500" :class="inputErrorClass('agreement')" @change="clearFieldError('agreement')" />
                    <label for="agreement" class="tw-m-0 tw-cursor-pointer tw-text-sm tw-font-normal tw-leading-6 tw-text-gray-600">
                        By ticking this box, you consent to abide by the
                        <a :href="`/terms-and-conditions`" target="_blank" class="tw-font-medium tw-text-orange-600 tw-underline tw-underline-offset-2">Terms and Conditions</a>
                        as well as
                        <a :href="`/privacy-policy`" target="_blank" class="tw-font-medium tw-text-orange-600 tw-underline tw-underline-offset-2">Privacy Policy</a>
                        outlined herein. Please review the terms carefully before proceeding. Your acceptance indicates your understanding and agreement to be bound by these terms.
                    </label>
                    <p v-if="form_error_data.agreement" class="tw-col-start-2 tw-m-0 tw-text-sm tw-text-red-600">{{ form_error_data.agreement }}</p>
                </div>

                <div class="tw-mt-6 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row tw-justify-end">
                    <a
                        href="/my-orders"
                        class="tw-inline-flex tw-min-h-11 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-gray-700 tw-shadow-sm tw-no-underline tw-transition hover:tw-bg-gray-50 hover:tw-no-underline sm:tw-w-auto"
                    >
                        Back
                    </a>

                    <button
                        type="submit"
                        class="tw-inline-flex tw-min-h-11 tw-w-full tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-orange-500 tw-bg-orange-500 tw-px-5 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white tw-shadow-sm tw-transition hover:tw-border-orange-600 hover:tw-bg-orange-600 disabled:tw-cursor-not-allowed disabled:tw-opacity-70 sm:tw-w-auto"
                        :class="{ 'tw-border-green-600 tw-bg-green-600 hover:tw-border-green-600 hover:tw-bg-green-600': isSubmitted }"
                        :disabled="isSubmitting || isSubmitted"
                    >
                        {{ isSubmitted ? "Submitted" : (isSubmitting ? "Submitting..." : "Submit") }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { getCities } from "malaysia-postcodes";
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';

export default {
    components: { Select, InputText },
    props: ["customer_session_id"], 
    data() {
        return {
            request_name: "",
            countries_list: [],
            stateOptions: [],
            logo_file_path: "",
            phone_instance: null,
            formData: {
                voucher_no: '',
                voucher_type: '',
                transaction_date: '',
                accept_privacy_policy_and_tac: false,
                expired_at: null,

                //Personal details
                full_name: '',
                email_address: '',
                phone_number: '',
                customer_type: 'Individual',

                //Individual details
                identification_number_type: '',
                identification_number: '',
                individual_tin: '',

                //Company details
                company_name: '',
                company_id: '',
                company_tin: '',
                sst_number: '',

                //Address details
                address_line_1: '',
                address_line_2: '',
                country: '',
                state: '',
                city: '',
                postal_code: '',
            },
            form_error_data: {},
            isSubmitting: false, // Tracks if the form is being submitted
            isSubmitted: false,
            error: false,
            support_email_address: "",
            isPermitted: null,
            isExpired: false,
        };
    },
    computed: {
        filteredStateOptions() {
            // Filter out "All State"
            return this.stateOptions.filter(option =>
                // option.state !== 'All States'
                option.name !== "00"
            )
        },
        cityPlaceholder() {
            if (!this.formData.country) {
                return "Please select country first";
            }

            if (!this.formData.state) {
                return "Please select state first";
            }

            return "Select City";
        }
    },
    watch: {
        "formData.customer_type": function (newVal) {
            this.form_error_data = {};
            // Clear irrelevant fields based on the selected customer type
            if (newVal === "Individual") {
                this.formData.company_name = "";
                this.formData.company_id = "";
                this.formData.company_tin = "";
                this.formData.sst_number = "";
            } else if (newVal === "Company") {
                this.formData.individual_address = "";
                this.formData.identification_number_type = "";
                this.formData.identification_number = "";
                this.formData.individual_tin = "";
            }
        },
        "formData.country": function (newVal) {
            if (!this.isSubmitted) {
                if (newVal != "Malaysia") {
                    this.formData.state = "17";
                }
                else {
                    this.formData.state = "";
                }
                this.clearFieldError('country');
                this.clearFieldError('state');
                this.clearFieldError('city');
            }
        },
        isPermitted(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.initPhoneInput();
                });
            }
        }
    },
    methods: {
        getRequestNameFromQuery() {
            const params = new URLSearchParams(window.location.search);
            return params.get("request") || "";
        },
        getPlaceholder(type) {
            switch (type) {
                case 'MyKad': return 'e.g. 971213081234';
                case 'Passport': return 'e.g. A12345678';
                case 'MyTentera': return 'e.g. 971213081234';
                default: return 'Please select identification number type!';
            }
        },
        cities() {
            //Convert state code (.name) to state name (.state)
            let state_name = "";
            const selectedState = this.stateOptions.find(
                state_code => state_code.name === this.formData.state
            );
            if (selectedState) {
                state_name = selectedState.state;
            }

            // Return cities dictionary for primevue Select component
            if (state_name === 'Wilayah Persekutuan Kuala Lumpur') //The library only recognise "Wp Kuala Lumpur", "Wp Labuan" and "Wp Putrajaya"
                return ['Kuala Lumpur']
            else if (state_name === 'Wilayah Persekutuan Labuan')
                return ["Labuan"]
            else if (state_name === 'Wilayah Persekutuan Putrajaya')
                return ["Putrajaya"]
            else if (state_name === 'Pulau Pinang')
                state_name = "Penang"
            return getCities(state_name).map(city => {return city;});
        },
        inputErrorClass(field) {
            return this.form_error_data[field] ? 'tw-field-invalid' : '';
        },
        choiceClass(field, isSelected) {
            return {
                'tw-border-orange-600 tw-bg-orange-100 tw-rounded-md': isSelected,
                'tw-border-red-600 tw-bg-red-50': this.form_error_data[field],
            };
        },
        clearFieldError(field) {
            if (this.form_error_data[field]) {
                delete this.form_error_data[field];
            }
        },
        addFieldError(field, message) {
            this.form_error_data[field] = message;
            return false;
        },
        isBlank(value) {
            return value === null || value === undefined || String(value).trim() === '';
        },
        validateRequiredFields() {
            let isValid = true;
            const requiredFields = [
                ['full_name', this.formData.full_name, 'Please enter your full name.'],
                ['customer_type', this.formData.customer_type, 'Please select whether you are claiming as an individual or company.'],
                ['address_line_1', this.formData.address_line_1, 'Please enter address line 1.'],
                ['country', this.formData.country, 'Please select a country.'],
                ['city', this.formData.city, 'Please enter or select a city.'],
                ['postal_code', this.formData.postal_code, 'Please enter a postal code.'],
            ];

            if (this.formData.country === 'Malaysia') {
                requiredFields.push(['state', this.formData.state, 'Please select a state.']);
            }

            if (this.formData.customer_type === 'Individual') {
                requiredFields.push(
                    ['identification_number_type', this.formData.identification_number_type, 'Please select an individual ID type.'],
                    ['identification_number', this.formData.identification_number, 'Please enter an individual ID.'],
                    ['individual_tin', this.formData.individual_tin, 'Please enter an individual TIN.'],
                );
            }

            if (this.formData.customer_type === 'Company') {
                requiredFields.push(
                    ['company_name', this.formData.company_name, 'Please enter the company name.'],
                    ['company_id', this.formData.company_id, 'Please enter a BRN.'],
                    ['company_tin', this.formData.company_tin, 'Please enter a company TIN.'],
                );
            }

            requiredFields.forEach(([field, value, message]) => {
                if (this.isBlank(value)) {
                    isValid = this.addFieldError(field, message);
                }
            });

            if (!this.formData.accept_privacy_policy_and_tac) {
                isValid = this.addFieldError('agreement', 'Please accept the terms and privacy policy before submitting.');
            }

            return isValid;
        },
        validatePhoneNumber(phone_input) {
            if (!phone_input || this.isBlank(phone_input.value)) {
                return this.addFieldError('phone_number', "Please enter a phone number.");
            }

            const localNumber = phone_input.value.replace(/\D/g, '');
            if (!localNumber || localNumber.length < 7 || localNumber.length > 15) {
                return this.addFieldError('phone_number', "Please enter a valid phone number.");
            }

            if (this.phone_instance) {
                const formatted_number = this.phone_instance.getNumber();
                if (this.phone_instance.isValidNumber()) {
                    this.formData.phone_number = formatted_number;
                    return true;
                }

                // intl-tel-input can return false before its utils script is ready.
                // Keep a sane local-number fallback so valid entries are not blocked.
                this.formData.phone_number = formatted_number || phone_input.value;
                return true;
            }

            this.formData.phone_number = phone_input.value;
            return true;
        },
        validate_form() {
            let isValid = true;
            this.form_error_data = {}; // Clear previous errors
            const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
            const passportPattern = /^[AHKahk]\d{8}$/;
            const individualTINPattern = /^(IG)(\d{9,11})$/;
            const nonIndividualTINPattern = /^[CDEF](\d{9,11})0$/;
            const nricPattern = /^\d{6}\d{2}\d{4}$/;
            const brnPattern = /^\d{4}(01|02|03|04|05|06)\d{6}$/;
            const sstRegNoPattern = /^[A-Z]\d{2}\d{4}\d{8}$/;
            const phone_input = document.querySelector("#phone");

            const patterns = {
                'MyKad': nricPattern,
                'MyTentera': nricPattern,
                'Passport': passportPattern,
            };

            if (!this.validateRequiredFields()) {
                isValid = false;
            }

            if (this.formData.customer_type === "Individual") {
                if (this.formData.identification_number_type && this.formData.identification_number) {
                    const pattern = patterns[this.formData.identification_number_type];
                    const errorMessage = `Please enter a valid ${this.formData.identification_number_type} number.`;
                    if (!pattern || !pattern.test(this.formData.identification_number)) {
                        isValid = this.addFieldError('identification_number', errorMessage);
                    }
                }

                if (this.formData.individual_tin && !individualTINPattern.test(this.formData.individual_tin)) {
                    isValid = this.addFieldError('individual_tin', "Please enter a valid TIN.");
                }
            }

            if (this.formData.customer_type === "Company") {
                if (this.formData.company_tin && !nonIndividualTINPattern.test(this.formData.company_tin)) {
                    isValid = this.addFieldError('company_tin', "Please enter a valid TIN.");
                }

                if (this.formData.company_id && !brnPattern.test(this.formData.company_id)) {
                    isValid = this.addFieldError('company_id', "Please enter a valid BRN.");
                }

                if (this.formData.sst_number !== "" && !sstRegNoPattern.test(this.formData.sst_number)) {
                    isValid = this.addFieldError('company_sst_number', "Please enter a valid SST Registration No.");
                }
            }

            if (!this.formData.email_address || !emailPattern.test(this.formData.email_address)) {
                isValid = this.addFieldError('email_address', "Please enter a valid email address.");
            }

            if (!this.validatePhoneNumber(phone_input)) {
                isValid = false;
            }

            this.$nextTick(() => {
                const firstInvalid = this.$el.querySelector('.tw-field-invalid, .tw-border-red-600');
                if (firstInvalid) {
                    firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            });

            return isValid;
        },
        submitForm() {
            if (this.isSubmitting || this.isSubmitted) {
                return;
            }

            // Frontend validation
            const isValid = this.validate_form();

            if (!isValid) {
                return;
            }

            const phoneInput = document.querySelector("#phone");

            if (this.phone_instance && phoneInput) {
                this.formData.phone_number = this.phone_instance.getNumber() || phoneInput.value;
            } else if (phoneInput) {
                this.formData.phone_number = phoneInput.value;
            }

            this.isSubmitting = true;
            this.form_error_data = {};

            const payload = {
                request_name: this.request_name,
                ...this.formData,
            };

            frappe.call({
                method: "winhoo.api.submit_sales_order_einvoice_request",
                args: {
                    formData: JSON.stringify(payload),
                },
                freeze: true,
                freeze_message: __("Submitting E-Invoice Request..."),

                callback: (response) => {
                    const message = response.message || {};

                    if (message.validation_error) {
                        this.isSubmitting = false;

                        this.form_error_data = message.field_errors || {};

                        frappe.msgprint({
                            title: __("Invalid"),
                            indicator: "red",
                            message: message.message || __("Please check the highlighted fields."),
                        });

                        this.$nextTick(() => {
                            const firstInvalid = this.$el.querySelector(
                                ".tw-field-invalid, .tw-border-red-600"
                            );

                            if (firstInvalid) {
                                firstInvalid.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                });
                            }
                        });

                        return;
                    }

                    if (message.success) {
                        this.isSubmitted = true;

                        frappe.show_alert({
                            title: __("Success"),
                            indicator: "green",
                            message: __("E-Invoice request submitted successfully."),
                        });

                        return;
                    }

                    this.isSubmitting = false;

                    frappe.msgprint({
                        title: __("Invalid"),
                        indicator: "red",
                        message: message.message || __("Unable to submit E-Invoice Request."),
                    });
                },
                error: () => {
                    this.isSubmitting = false;

                    frappe.msgprint({
                        title: __("Error"),
                        indicator: "red",
                        message: __("Something went wrong while submitting the E-Invoice Request."),
                    });
                },
            });
        },
        fetchEinvoiceRequestInfo() {
            if (!this.request_name) {
                this.isPermitted = false;
                return;
            }

            this.isPermitted = null;

            frappe.call({
                method: "winhoo.api.get_einvoice_request_info",
                args: {
                    request_name: this.request_name
                },
                callback: (response) => {
                    const doc = response.message;

                    if (!doc) {
                        this.isPermitted = false;
                        return;
                    }

                    this.isPermitted = true;

                    this.formData.voucher_type = doc.voucher_type || "";
                    this.formData.voucher_no = doc.voucher_no || "";
                    this.formData.transaction_date = doc.transaction_date || "";
                    this.formData.expired_at = doc.expired_at || "";

                    this.formData.full_name = doc.full_name || "";
                    this.formData.email_address = doc.email_address || "";
                    this.formData.phone_number = doc.mobile_number || "";
                    this.formData.customer_type = doc.customer_type || "Individual";

                    this.formData.identification_number_type = doc.identification_number_type || "";
                    this.formData.identification_number = doc.identification_number || "";
                    this.formData.individual_tin = doc.individual_tin || "";

                    this.formData.company_name = doc.company_name || "";
                    this.formData.company_id = doc.company_id || "";
                    this.formData.company_tin = doc.company_tin || "";
                    this.formData.sst_number = doc.sst_registration_no || "";

                    this.formData.address_line_1 = doc.address_line_1 || "";
                    this.formData.address_line_2 = doc.address_line_2 || "";
                    this.formData.country = doc.country || "";
                    this.formData.state = doc.state || "";
                    this.formData.city = doc.city || "";
                    this.formData.postal_code = doc.postal_code || "";
                    this.formData.accept_privacy_policy_and_tac = Boolean(doc.accept_privacy_policy_and_tac);
                    this.formData.submitted_in_consolidated_einvoice = doc.submitted_in_consolidated_einvoice;

                    this.isSubmitted = doc.docstatus === 1;

                    if (this.phone_instance && doc.mobile_number) {
                        this.phone_instance.setNumber(doc.mobile_number);
                    }

                    this.isExpired = new Date(doc.expired_at) < new Date();
                }
            });
        },
        initPhoneInput() {
            const phone_input = document.querySelector("#phone");
            if (!phone_input) {
                console.error("Phone input element not found!");
                return;
            }
            this.phone_instance = intlTelInput(phone_input, {
                initialCountry: "my",
                loadUtils: () => import("https://cdn.jsdelivr.net/npm/intl-tel-input@25.2.0/build/js/utils.js"),
                separateDialCode: true,
                useFullscreenPopup: false,
                autoPlaceholder: "aggressive"
            });
            phone_input.addEventListener("input", this.update_phone_number);
            phone_input.addEventListener("countrychange", this.update_phone_number);
        },
        update_phone_number(type) {
            if (type === "phone" && this.phone_instance) {
                const formatted_number = this.phone_instance.getNumber();
                const country_iso2 = this.phone_instance.getSelectedCountryData().iso2;
                const is_valid = this.phone_instance.isValidNumber();

                if (is_valid !== false) {
                    this.formData.contact_number = formatted_number;
                    this.formData.country_iso2 = country_iso2;
                }
            }
        },
        ignore_dash_character(event, field) {
            const inputElement = event.target;
            const value = inputElement.value.replace(/-/g, ''); // Remove "-" from input while typing in.

            this.formData[field] = value;
            inputElement.value = value;
        },
        handleDashlessInput(event, field) {
            this.ignore_dash_character(event, field);
            this.clearFieldError(field);
        },
        get_country_and_state_codes() {
            frappe.call({
                method: "myinvois.api.get_country_and_state_codes",
                args: {},
                callback: (response) => {
                    if (response.message) {
                        this.stateOptions = response.message.state_codes;
                        this.countries_list = response.message.countries;
                    }
                }
            });
        },
    },
    mounted() {
        this.request_name = this.getRequestNameFromQuery();
        this.fetchEinvoiceRequestInfo();
        this.get_country_and_state_codes();
        this.$nextTick(() => {
            this.initPhoneInput();
        });
    },
    beforeUnmount() {
        // Clean up the intl-tel-input instance
        if (this.phone_instance) {
            this.phone_instance.destroy();
        }
    },
};
</script>

<style scoped>
:deep(.iti) {
    width: 100%;
}

:deep(.pv-select) {
    width: 100%;
    min-height: 2.75rem;
    border-color: rgb(209 213 219);
    border-radius: 0.375rem;
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.05);
}

:deep(.pv-select:not(.pv-disabled).pv-focus) {
    border-color: rgb(249 115 22);
    box-shadow: 0 0 0 2px rgb(255 237 213);
}

:deep(.pv-select-label) {
    padding: 0.625rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
}

:deep(.pv-disabled) {
    background-color: rgb(243 244 246);
    color: rgb(107 114 128);
    opacity: 1;
}

.tw-field-invalid,
:deep(.tw-field-invalid),
:deep(.tw-field-invalid.pv-select) {
    border-color: rgb(220 38 38) !important;
}

.tw-field-invalid:focus,
:deep(.tw-field-invalid:not(.pv-disabled).pv-focus),
:deep(.tw-field-invalid:focus) {
    border-color: rgb(220 38 38) !important;
    box-shadow: 0 0 0 2px rgb(254 226 226) !important;
}
</style>